import { useState } from 'react';
import type { ProviderDetail } from '../../../../../js/api';
import { Badge } from '../../../components/badge';
import { useHasPermission } from '../../../hooks/useHasPermission';
import {
  CredentialEntriesTab,
  ExpirationDate,
  PROVIDER_UPDATE_PERMISSION,
} from './CredentialEntriesTab';
import { CREDENTIAL_SECTIONS } from './credentials';
import { ImlcDialog } from './ImlcDialog';
import { ProviderDrawerMaps, providerFileName } from './providerDisplay';
import { EditPencil } from './ProviderDrawerPrimitives';

export { ExpirationDate, PROVIDER_UPDATE_PERMISSION };

/**
 * Licenses, board certifications and life-support certifications as credential
 * ENTRIES with a backing document (`CredentialEntriesTab`), with the compact-license
 * (IMLC) fields on top.
 */
export function LicensesTab({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const canUpdate = useHasPermission(PROVIDER_UPDATE_PERMISSION);
  const [imlcOpen, setImlcOpen] = useState(false);

  return (
    <>
      <CredentialEntriesTab
        provider={provider}
        maps={maps}
        sections={CREDENTIAL_SECTIONS}
        testIdPrefix="provider-licenses"
        zipSubject={`${providerFileName(provider)} Licenses and Certifications Documents`}
        emptyText="No licenses or certifications on file."
        loadingText="Loading credentials..."
        header={
          <ImlcLine provider={provider} canUpdate={canUpdate} onEdit={() => setImlcOpen(true)} />
        }
      />
      <ImlcDialog open={imlcOpen} onClose={() => setImlcOpen(false)} provider={provider} />
    </>
  );
}

/** The compact-license line: holder or not, expiry, and the pencil to change it. */
function ImlcLine({
  provider,
  canUpdate,
  onEdit,
}: {
  provider: ProviderDetail;
  canUpdate: boolean;
  onEdit: () => void;
}) {
  return (
    <div
      className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:rounded-md tw:bg-brand-green-3 tw:px-2 tw:py-1 tw:text-sm"
      data-testid="provider-licenses-imlc"
    >
      <span className="tw:text-brand-green-0 tw:italic">Compact License (IMLC)</span>
      <span className="tw:text-zinc-800">
        {provider.has_imlc ? (
          <>
            <Badge color="green">Holder</Badge>
            {provider.imlc_expiration_date && (
              <>
                {' · expires '}
                <ExpirationDate value={provider.imlc_expiration_date} showRelative />
              </>
            )}
          </>
        ) : (
          <span className="tw:text-zinc-500">Not a compact-license holder</span>
        )}
      </span>
      {canUpdate && (
        <EditPencil onClick={onEdit} title="Edit IMLC" data-testid="provider-licenses-imlc-edit" />
      )}
    </div>
  );
}
